// PatientContext.tsx
import React, { createContext, useState, useContext, useEffect, useRef, ReactNode, SetStateAction } from 'react';
import axios from 'axios';
import profileApi from '../offline/profileApi';
import { cacheOwner, cachedPatients, rememberPatients } from '../offline/profileCache';
import { message } from 'antd';
import { Patient, Receipt } from './type';
import dayjs from 'dayjs';
import API from '../config/api';
import { useClinicContext } from './ClinicContext';

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number | null;
  prevPage: number | null;
}

interface PatientContextType {
  patients: Patient[];
  filteredPatients: Patient[];
  selectedPatient: Patient | null;
  setSelectedPatient: (update: SetStateAction<Patient | null>) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  setDateRange: (range: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => void;
  isDateFilterVisible: boolean;
  setIsDateFilterVisible: (visible: boolean) => void;
  /**
   * Drop every entry in the short-lived patient-list response cache.
   * Call this when an external event (Firestore listener, push notification,
   * etc.) signals that the patient list may have changed before the actual
   * refresh fetch runs. The next `fetchPatients()` call will then go to the
   * network even without `forceRefresh: true`.
   */
  clearPatientListCache: () => void;
  /**
   * Optimistically insert (or move to the top) a patient in the local list.
   * Use this right after a successful write so the patient is visible in
   * the dashboard without waiting for the follow-up GET /api/patients
   * round-trip. The background refresh will reconcile shortly after.
   */
  upsertPatientLocally: (patient: Patient) => void;
  fetchPatients: (options?: {
    search?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    /**
     * Force a network round-trip even when the response cache or an in-flight
     * request would satisfy this call. Use after a write so the new state is
     * read back from the server.
     */
    forceRefresh?: boolean;
  }) => Promise<{ patients: Patient[]; pagination?: PaginationInfo | null }>;
  clearFilters: () => void;
  selectedReceipt: Receipt | null;
  setSelectedReceipt: (receipt: Receipt | null) => void;
  isViewReceiptModalVisible: boolean;
  setIsViewReceiptModalVisible: (visible: boolean) => void;
  forceRender: number;
  setForceRender: (value: number) => void;
  setFilteredPatients: (patients: Patient[]) => void;
  pagination: PaginationInfo | null;
}

const PatientContext = createContext<PatientContextType | undefined>(undefined);

export const PatientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [isDateFilterVisible, setIsDateFilterVisible] = useState<boolean>(false);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [isViewReceiptModalVisible, setIsViewReceiptModalVisible] = useState<boolean>(false);
  const [forceRender, setForceRender] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);

  // Get doctor_id from localStorage (set during login from Firebase /doctor_clinic_assistant collection)
  // Note: The Firebase UID is the assistant_id, but we need doctor_id for API calls
  // Make it reactive so it updates when doctorId changes in localStorage
  const [userId, setUserId] = useState<string | null>(localStorage.getItem('doctorId'));
  const { selectedClinicId, clinicScopeIds } = useClinicContext();
  
  // Listen for changes to doctorId in localStorage (e.g., after login)
  useEffect(() => {
    const checkDoctorId = () => {
      const currentDoctorId = localStorage.getItem('doctorId');
      if (currentDoctorId !== userId) {
        console.log('doctorId changed in localStorage:', currentDoctorId);
        setUserId(currentDoctorId);
      }
    };
    
    // Check immediately
    checkDoctorId();
    
    // Listen for custom event (when doctorId is set during login in same tab)
    window.addEventListener('doctorIdChanged', checkDoctorId);
    
    // Listen for storage events (when localStorage changes in another tab/window)
    window.addEventListener('storage', checkDoctorId);
    
    return () => {
      window.removeEventListener('doctorIdChanged', checkDoctorId);
      window.removeEventListener('storage', checkDoctorId);
    };
  }, [userId]); 

  useEffect(() => {
    const synchronized = (event: Event) => {
      const { localId, patient } = (event as CustomEvent).detail || {};
      if (!localId || !patient?._id) return;
      const replace = (rows: Patient[]) => rows.map(row => row._id === localId ? { ...row, ...patient, _offlineDraft: false, offlinePending: false } as Patient : row);
      setPatients(replace); setFilteredPatients(replace);
      setSelectedPatient(current => current?._id === localId ? { ...current, ...patient, _offlineDraft: false, offlinePending: false } as Patient : current);
    };
    window.addEventListener('offlinePatientSynchronized', synchronized);
    return () => window.removeEventListener('offlinePatientSynchronized', synchronized);
  }, []);

  useEffect(() => {
    const waitingOnly = (event: Event) => {
      const ids = new Set<string>(((event as CustomEvent).detail?.ids || []).map(String));
      if (!ids.size) return;
      const matches = (patient: Patient) =>
        [patient._id, patient.patient_id].some(id => id && ids.has(String(id)));
      setPatients(current => current.filter(patient => !matches(patient)));
      setFilteredPatients(current => current.filter(patient => !matches(patient)));
      setSelectedPatient(current => current && matches(current) ? null : current);
    };
    window.addEventListener('offlinePatientWaitingOnly', waitingOnly);
    return () => window.removeEventListener('offlinePatientWaitingOnly', waitingOnly);
  }, []);
  
  // Sort function to sort by date (newest first) and then by _id for same dates
  // Handles both 'date' and 'last_visit_date' fields (history endpoint uses last_visit_date)
  const sortByLatestDate = (data: Patient[]) => {
    return [...data].sort((a: Patient, b: Patient) => {
      // Use date or last_visit_date (history endpoint format)
      const dateAStr = a.date || a.last_visit_date;
      const dateBStr = b.date || b.last_visit_date;
      
      // Handle missing dates
      if (!dateAStr) return 1;
      if (!dateBStr) return -1;
      
      const dateA = new Date(dateAStr).getTime();
      const dateB = new Date(dateBStr).getTime();
      
      // If dates are different, sort by date (newest first)
      if (dateA !== dateB) {
        return dateB - dateA;
      }
      
      // If dates are the same, sort by _id to put newest first
      if (a._id && b._id) {
        return b._id.localeCompare(a._id);
      }
      
      return 0;
    });
  };

  // Custom setSelectedPatient: supports direct value or functional update (like useState)
  const safeSetSelectedPatient = (update: SetStateAction<Patient | null>) => {
    if (typeof update === 'function') {
      setSelectedPatient(prev => {
        const next = update(prev);
        console.log('Setting selected patient:', next ? next.patient_name : 'null');
        if (next === null) {
          setSelectedReceipt(null);
          setIsViewReceiptModalVisible(false);
        }
        return next;
      });
    } else {
      console.log('Setting selected patient:', update ? update.patient_name : 'null');
      setSelectedPatient(update);
      if (update === null) {
        setSelectedReceipt(null);
        setIsViewReceiptModalVisible(false);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Request coalescing / short-lived response cache for `/api/patients`.
  //
  // Why: the same patient-list URL was being requested up to 3× in a single
  // burst (mount fires from PatientContext + PatientsList, Firestore-driven
  // refresh fires from Dashboard + PatientsList refreshTrigger, etc.). Each
  // burst hit the network with the same params and received the same payload.
  //
  // Strategy:
  //   1. In-flight dedup — a concurrent call with the same params returns the
  //      Promise that's already running instead of opening a second request.
  //   2. 5-second response cache — a follow-up call within the freshness
  //      window resolves locally without a network round-trip. The window is
  //      intentionally small so post-write reads still see fresh data quickly.
  //      Any caller that has just performed a mutation can pass
  //      { forceRefresh: true } to bypass both layers.
  // ─────────────────────────────────────────────────────────────────────────
  const RESPONSE_CACHE_TTL_MS = 5000;
  const responseCacheRef = useRef<Map<string, { result: { patients: Patient[]; pagination?: PaginationInfo | null }; at: number }>>(new Map());
  const inFlightRef = useRef<Map<string, Promise<{ patients: Patient[]; pagination?: PaginationInfo | null }>>>(new Map());

  // Public escape hatch: drop every cached patient-list response right now.
  // Used by callers that know a mutation has just happened (e.g. Dashboard's
  // Firestore listener) but want any non-forceRefresh fetch that arrives
  // before the debounced auto-refresh to still observe fresh data.
  // Note: in-flight requests are intentionally NOT cancelled — callers that
  // are already waiting on the network get the same fresh response.
  const clearPatientListCache = () => {
    if (responseCacheRef.current.size > 0) {
      console.log('[PatientContext] clearPatientListCache: dropping',
        responseCacheRef.current.size, 'cached response(s)');
      responseCacheRef.current.clear();
    }
  };

  // Optimistic insertion: prepend `patient` to the local lists, or — if a
  // record with the same patient_id already exists — merge the incoming
  // fields onto it and move it to the top. Used to give instant visual
  // feedback after a write without waiting for the follow-up GET
  // /api/patients round-trip.
  //
  // Merge semantics are intentional: this function is called from two
  // sources that carry different amounts of data —
  //   1. SidebarWaitingList.handleCheckIn passes the full POST response
  //      (visits[], receipts[], voice_notes, obgynHistory, …).
  //   2. Dashboard's Firestore listener passes a minimal record built from
  //      the waiting-list doc (name, phone, date, time, file_number, …).
  // Both calls can fire within a few hundred ms of each other. With merge
  // semantics, the second call enriches the row instead of clobbering the
  // first one's heavy fields with empty strings.
  const upsertPatientLocally = (patient: Patient) => {
    if (!patient || !patient.patient_id) {
      return;
    }
    const isMeaningful = (v: any) =>
      v !== undefined && v !== null && v !== '' &&
      !(Array.isArray(v) && v.length === 0);
    const mergeInto = (existing: Patient): Patient => {
      const merged: any = { ...existing };
      Object.entries(patient).forEach(([key, value]) => {
        if (isMeaningful(value)) {
          merged[key] = value;
        }
      });
      return merged as Patient;
    };
    const matches = (p: Patient) => p.patient_id === patient.patient_id;
    setPatients(prev => {
      const existing = prev.find(matches);
      const row = existing ? mergeInto(existing) : patient;
      const others = prev.filter(p => !matches(p));
      return [row, ...others];
    });
    setFilteredPatients(prev => {
      const existing = prev.find(matches);
      const row = existing ? mergeInto(existing) : patient;
      const others = prev.filter(p => !matches(p));
      return [row, ...others];
    });
    // Drop any cached list response so the next non-force fetch reflects
    // the new ordering instead of replaying the pre-write snapshot.
    responseCacheRef.current.clear();
  };

  // Fetch patients from API with optional filters
  const fetchPatients = async (options?: {
    search?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    forceRefresh?: boolean;
  }) => {
    // Always apply pagination defaults so we never fetch all records at once
    const resolvedOptions = {
      page: 1,
      limit: 20,
      sortBy: 'date',
      sortOrder: 'desc',
      ...options,
    };
    const forceRefresh = options?.forceRefresh === true;
    const offlineScope = JSON.stringify([selectedClinicId, clinicScopeIds]);
    if (!navigator.onLine && cacheOwner()) {
      const saved = await cachedPatients(offlineScope, resolvedOptions.search || '');
      const filtered = saved.filter((patient: any) => {
        const date = String(patient.date || patient.last_visit_date || '').slice(0, 10);
        return (!resolvedOptions.startDate || date >= resolvedOptions.startDate.slice(0, 10)) &&
          (!resolvedOptions.endDate || date <= resolvedOptions.endDate.slice(0, 10)) &&
          (!resolvedOptions.status || patient.status === resolvedOptions.status);
      });
      const ordered = sortByLatestDate(filtered);
      const totalPages = Math.ceil(ordered.length / resolvedOptions.limit);
      const page = ordered.slice((resolvedOptions.page - 1) * resolvedOptions.limit, resolvedOptions.page * resolvedOptions.limit);
      const info = { currentPage: resolvedOptions.page, totalPages, totalItems: ordered.length, itemsPerPage: resolvedOptions.limit,
        hasNextPage: resolvedOptions.page < totalPages, hasPrevPage: resolvedOptions.page > 1,
        nextPage: resolvedOptions.page < totalPages ? resolvedOptions.page + 1 : null, prevPage: resolvedOptions.page > 1 ? resolvedOptions.page - 1 : null };
      setPatients(page); setFilteredPatients(page); setPagination(info);
      return { patients: page, pagination: info };
    }

    // Cache key must include every input that can change the response.
    // doctorId / selectedClinicId / clinicScopeIds are part of the request,
    // but they're also captured by the resolved request params built below.
    // We key off a stable JSON of the inputs we know up-front; the network
    // params are derived from these.
    const cacheKey = JSON.stringify({
      userId,
      selectedClinicId,
      clinicScopeIds,
      page: resolvedOptions.page,
      limit: resolvedOptions.limit,
      sortBy: resolvedOptions.sortBy,
      sortOrder: resolvedOptions.sortOrder,
      search: resolvedOptions.search || '',
      startDate: resolvedOptions.startDate || '',
      endDate: resolvedOptions.endDate || '',
      status: resolvedOptions.status || '',
    });

    if (!forceRefresh) {
      // 1) Coalesce with any in-flight identical request.
      const pending = inFlightRef.current.get(cacheKey);
      if (pending) {
        console.log('[PatientContext] fetchPatients: joining in-flight request', cacheKey.slice(0, 120));
        return pending;
      }
      // 2) Serve from the short-lived response cache when it's still fresh.
      const cached = responseCacheRef.current.get(cacheKey);
      if (cached && Date.now() - cached.at < RESPONSE_CACHE_TTL_MS) {
        console.log('[PatientContext] fetchPatients: response cache hit (age',
          Date.now() - cached.at, 'ms)');
        return cached.result;
      }
    }

    const run = async () => {
    setIsLoading(true);
    try {
      // Use the /api/patients endpoint which supports filtering by doctor_id, clinic_id, and assistant_id
      // This endpoint returns data sorted by most recent first (newest to oldest)
      let endpoint = `${API.BASE_URL}${API.ENDPOINTS.PATIENTS}`;

      // Build query parameters - only essential parameters like mobile app
      // Simple GET request with URL parameters only (no payload/body)
      const params: any = {};
      
      // SECURITY: doctor_id is required - always send it from localStorage
      // IMPORTANT: Make sure we're using doctorId, NOT clinicId
      if (!userId) {
        console.log('doctorId not found in localStorage yet - waiting for login to complete');
        // Don't show error during initialization - just return empty
        // The useEffect will retry when doctorId is set
        return { patients: [], pagination: null };
      }
      
      // Debug: Log what we're getting from localStorage
      console.log('doctorId from localStorage:', userId);
      console.log('selectedClinicId:', selectedClinicId);
      
      // Ensure we're using doctorId, not clinicId
      // Double-check that userId is actually the doctorId, not clinicId
      if (userId === selectedClinicId) {
        console.error('ERROR: doctorId matches clinicId - this is wrong!');
        message.error('Configuration error: Doctor ID and Clinic ID are the same. Please log in again.');
        return { patients: [], pagination: null };
      }
      
      params.doctor_id = userId;

      // "All Clinics" mode: query all clinics in the shared-file scope together.
      // Single clinic mode: filter by the selected clinic only.
      if (selectedClinicId === '__all_clinics__' && clinicScopeIds && clinicScopeIds.length > 0) {
        params.clinic_ids = clinicScopeIds.join(',');
        params.use_clinic_scope = 'true';
      } else if (selectedClinicId && selectedClinicId !== '__all_clinics__') {
        params.clinic_id = selectedClinicId;
      }
      
      params.page = resolvedOptions.page;
      params.limit = resolvedOptions.limit;
      params.sortBy = resolvedOptions.sortBy;
      params.sortOrder = resolvedOptions.sortOrder;
      if (forceRefresh) {
        params.bypassCache = 'true';
      }
      if (resolvedOptions.search) {
        params.search = resolvedOptions.search;
      }

      // Always send params (backend handles pagination only when page/limit are provided)
      // This maintains backward compatibility - if no pagination params, backend returns all results
      console.log('Fetching from endpoint:', endpoint);
      console.log('Request params:', JSON.stringify(params, null, 2));
      console.log('VERIFY: doctor_id param =', params.doctor_id);
      console.log('VERIFY: This should NOT equal clinicId:', selectedClinicId);
      
      // Simple GET request with URL parameters only (no body/payload)
      // Matches mobile app behavior exactly
      const response = await profileApi.get(endpoint, {
        params,
        timeout: 30000 // 30 second timeout
      });
      console.log('API Response received');

      // Handle /api/patients endpoint response format: { success, message, data, totalItems, filters }
      // Backend already sorts by most recent first (newest to oldest) - no need to sort again
      let updatedPatients: Patient[];
      let paginationInfo: PaginationInfo | null = null;
      
      if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        // New format from /api/patients endpoint
        updatedPatients = response.data.data;
        // Use pagination info directly from response if available
        if (response.data.pagination) {
          paginationInfo = response.data.pagination;
        } else if (response.data.totalItems !== undefined) {
          // Fallback: build pagination info from totalItems
          const currentPage = resolvedOptions.page;
          const itemsPerPage = resolvedOptions.limit;
          paginationInfo = {
            currentPage,
            totalPages: Math.ceil(response.data.totalItems / itemsPerPage),
            totalItems: response.data.totalItems,
            itemsPerPage,
            hasNextPage: currentPage < Math.ceil(response.data.totalItems / itemsPerPage),
            hasPrevPage: currentPage > 1,
            nextPage: currentPage < Math.ceil(response.data.totalItems / itemsPerPage) ? currentPage + 1 : null,
            prevPage: currentPage > 1 ? currentPage - 1 : null
          };
        }
      } else if (Array.isArray(response.data)) {
        // Backward compatibility - direct array response
        updatedPatients = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        // Alternative format with data property
        updatedPatients = response.data.data;
      } else {
        updatedPatients = [];
      }

      console.log('Raw patients data:', updatedPatients);
      console.log('Pagination info:', paginationInfo);
      
      // Backend already sorts by latest visit date (newest to oldest).
      // No need for client-side sorting - backend handles it
      const sortedPatients = updatedPatients;
      void rememberPatients(sortedPatients, offlineScope).catch(() => {});
      console.log('About to update state with:', sortedPatients);

      // Update state with new data
      // Preserve the backend's latest-visit ordering.
      setPatients([...sortedPatients]);
      // Set filtered patients - useEffect will handle filtering if needed
      setFilteredPatients([...sortedPatients]);
      setPagination(paginationInfo);
      console.log('State updated with patient data');
      
      // If there's a selected patient, update it with fresh data.
      // The list endpoint omits heavy fields (voice_notes, audioData, etc.),
      // so we merge them in from the current selectedPatient to avoid losing them.
      if (selectedPatient) {
        const freshPatient = updatedPatients.find((p: Patient) => p._id === selectedPatient._id);
        if (freshPatient) {
          if (freshPatient.receipts && freshPatient.receipts.length > 0) {
            freshPatient.receipts = [...freshPatient.receipts].sort((a: Receipt, b: Receipt) => 
              new Date(b.date).getTime() - new Date(a.date).getTime()
            );
          }
          setSelectedPatient(currentPatient => {
            // The request may finish after the user has returned to the list or
            // opened a different patient. Never restore the stale selection.
            if (!currentPatient || currentPatient._id !== freshPatient._id) {
              return currentPatient;
            }

            return {
              ...freshPatient,
              // Preserve detail-only fields the list endpoint does not return
              voice_notes: freshPatient.voice_notes ?? currentPatient.voice_notes,
              obgynHistory: freshPatient.obgynHistory ?? currentPatient.obgynHistory,
            };
          });
        }
      }
      
      // Force re-render of the table to maintain sort order
      setForceRender(prev => prev + 1);
      
      const result = { patients: sortedPatients, pagination: paginationInfo };
      // Cache the successful result so the next identical call within the
      // freshness window resolves locally instead of hitting the network.
      responseCacheRef.current.set(cacheKey, { result, at: Date.now() });
      return result;
    } catch (error: any) {
      console.error('Error fetching patients', error);
      console.error('Error details:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch patients';
      message.error(`Failed to fetch patients: ${errorMessage}`);
      return { patients: [], pagination: null };
    } finally {
      setIsLoading(false);
    }
    };

    // Register the promise so concurrent identical callers join this run
    // instead of opening a duplicate request. Clean up on settle either way.
    const promise = run();
    inFlightRef.current.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      inFlightRef.current.delete(cacheKey);
    }
  };

  // Apply all filters: search term and date range
  const applyFilters = () => {
    console.log('Applying filters to', patients.length, 'patients');
    
    // If no patients, clear filtered patients
    if (patients.length === 0) {
      setFilteredPatients([]);
      return;
    }
    
    let filtered = [...patients];
    
    // Apply search filter
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(patient => 
        (patient.patient_name?.toLowerCase().includes(lowerSearchTerm)) ||
        (patient.patient_phone?.toLowerCase().includes(lowerSearchTerm)) ||
        (patient.email?.toLowerCase().includes(lowerSearchTerm)) ||
        (patient.address?.toLowerCase().includes(lowerSearchTerm)) ||
        ((patient as any).file_number?.toLowerCase().includes(lowerSearchTerm))
      );
    }
    
    // Apply date filter
    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(patient => {
        if (!patient.date) return false;
        
        const patientDate = dayjs(patient.date);
        const startDate = dateRange[0]?.startOf('day');
        const endDate = dateRange[1]?.endOf('day');
        
        return (patientDate.isAfter(startDate) || patientDate.isSame(startDate)) && 
               (patientDate.isBefore(endDate) || patientDate.isSame(endDate));
      });
    }
    
    // Don't re-sort - backend already sorted by latest visit date.
    // Just apply the filters and preserve the API sort order
    // Always set filteredPatients, even if empty (to show "no results")
    setFilteredPatients(filtered);
    console.log('Filtered patients updated:', filtered.length, 'patients match filters');
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setDateRange([null, null]);
    setFilteredPatients([...patients]);
  };

  // Single combined effect: re-fetch whenever the doctor, clinic selection, or clinic scope
  // changes. A 500 ms debounce ensures that rapid sequential state updates from ClinicContext
  // (userId resolves, then selectedClinicId resolves, then clinicScopeIds resolves) all
  // collapse into ONE request instead of firing 3-4 times independently.
  useEffect(() => {
    if (!userId) return;
    const timer = setTimeout(() => {
      fetchPatients({ page: 1, limit: 20, sortBy: 'date', sortOrder: 'desc' });
    }, 500);
    return () => clearTimeout(timer);
  }, [userId, selectedClinicId, clinicScopeIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track whether the search effect has been initialized.
  // The initial empty-string fire is skipped because the combined effect above already
  // fetches the full list on mount. Only actual user-driven changes run a search request.
  const searchInitializedRef = useRef(false);

  // When searchTerm changes, fetch from API so all patients are searched (not just the loaded page)
  useEffect(() => {
    if (!userId) return;
    if (!searchInitializedRef.current) {
      searchInitializedRef.current = true;
      return; // skip — initial list is already being fetched by the combined effect
    }
    const term = searchTerm.trim();
    const timer = setTimeout(() => {
      fetchPatients({
        page: 1,
        limit: 20,
        sortBy: 'date',
        sortOrder: 'desc',
        ...(term ? { search: term } : {})
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // When dateRange changes, apply locally
  useEffect(() => {
    if (patients.length === 0) {
      setFilteredPatients([]);
      return;
    }
    if (dateRange[0] && dateRange[1]) {
      applyFilters();
    } else {
      setFilteredPatients([...patients]);
    }
  }, [dateRange, patients]);
  
  // Sort receipts by date when selected patient changes
  useEffect(() => {
    if (!selectedPatient) return;
    
    if (selectedPatient.receipts && selectedPatient.receipts.length > 0) {
      console.log('Sorting receipts for selected patient');
      const sortedReceipts = [...selectedPatient.receipts].sort((a: Receipt, b: Receipt) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      
      // Avoid unnecessary re-renders by checking if the order has actually changed
      const currentReceiptIds = selectedPatient.receipts.map(r => r._id).join(',');
      const sortedReceiptIds = sortedReceipts.map(r => r._id).join(',');
      
      if (currentReceiptIds !== sortedReceiptIds) {
        setSelectedPatient({
          ...selectedPatient,
          receipts: sortedReceipts
        });
      }
    }
  }, [selectedPatient?._id]);

  // Create value object for context
  const value = {
    patients,
    filteredPatients,
    selectedPatient,
    setSelectedPatient: safeSetSelectedPatient,
    searchTerm,
    setSearchTerm,
    dateRange,
    setDateRange,
    isDateFilterVisible,
    setIsDateFilterVisible,
    fetchPatients,
    clearFilters,
    selectedReceipt,
    setSelectedReceipt,
    isViewReceiptModalVisible,
    setIsViewReceiptModalVisible,
    forceRender,
    setForceRender,
    setFilteredPatients,
    pagination,
    clearPatientListCache,
    upsertPatientLocally,
  };

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
};

export const usePatientContext = () => {
  const context = useContext(PatientContext);
  if (context === undefined) {
    throw new Error('usePatientContext must be used within a PatientProvider');
  }
  return context;
};
